import { useState, useEffect, useMemo } from "react";
import { Filter, CheckCircle, Clock, AlertCircle, XCircle } from "lucide-react";
import { Button } from "../../primitives/Button";
import { Select } from "../../primitives/Select";
import { listTasks, type TaskInstance, type TaskOpsStatus, type TaskLifecycleStatus, type TaskHealthState } from "../../../api/tasks";
import { getProcessTemplate, type ProcessTemplate } from "../../../api/processes";
import { cn } from "../../utils/cn";

type ProcessActiveTasksTabProps = {
  templateId: string;
};

export function ProcessActiveTasksTab({ templateId }: ProcessActiveTasksTabProps) {
  const [template, setTemplate] = useState<ProcessTemplate | null>(null);
  const [tasks, setTasks] = useState<TaskInstance[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState<{
    status_ops?: TaskOpsStatus;
    status_lifecycle?: TaskLifecycleStatus;
    health_state?: TaskHealthState;
  }>({});
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    loadTemplate();
  }, [templateId]);

  useEffect(() => {
    if (template) {
      loadTasks();
    }
  }, [template, filters, page]);

  const loadTemplate = async () => {
    try {
      const data = await getProcessTemplate(templateId);
      setTemplate(data);
    } catch (error: any) {
      console.error("Failed to load process template:", error);
    }
  };

  const loadTasks = async () => {
    if (!template) return;
    setIsLoading(true);
    try {
      // Filter tasks by process template's ops_family
      const params: any = {
        page,
        page_size: 50,
      };
      if (filters.status_ops) params.status_ops = filters.status_ops;
      if (filters.status_lifecycle) params.status_lifecycle = filters.status_lifecycle;
      if (filters.health_state) params.health_state = filters.health_state;

      const data = await listTasks(params);
      // Filter by ops_family if template has one
      let filteredTasks = data.items;
      if (template.ops_family) {
        filteredTasks = data.items.filter((t) => t.ops_family === template.ops_family);
      }
      setTasks(filteredTasks);
      setTotal(data.meta.total);
    } catch (error: any) {
      console.error("Failed to load tasks:", error);
      alert(`Erreur lors du chargement des tâches: ${error.message || error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusLabel = (status: TaskOpsStatus) => {
    switch (status) {
      case "not_started":
        return "Non démarré";
      case "in_progress":
        return "En cours";
      case "blocked":
        return "Bloqué";
      case "completed":
        return "Terminé";
      default:
        return status;
    }
  };

  const getStatusTone = (status: TaskOpsStatus) => {
    switch (status) {
      case "not_started":
        return "neutral";
      case "in_progress":
        return "info";
      case "blocked":
        return "warning";
      case "completed":
        return "success";
      default:
        return "neutral";
    }
  };

  const getLifecycleLabel = (status: TaskLifecycleStatus) => {
    switch (status) {
      case "active":
        return "Actif";
      case "paused":
        return "En pause";
      case "archived":
        return "Archivé";
      case "cancelled":
        return "Annulé";
      default:
        return status;
    }
  };

  const getHealthIcon = (health: TaskHealthState) => {
    switch (health) {
      case "good":
        return <CheckCircle size={16} className="text-success" />;
      case "attention":
        return <Clock size={16} className="text-warning" />;
      case "critical":
        return <AlertCircle size={16} className="text-danger" />;
      default:
        return null;
    }
  };

  const getHealthLabel = (health: TaskHealthState) => {
    switch (health) {
      case "good":
        return "Bon";
      case "attention":
        return "Attention";
      case "critical":
        return "Critique";
      default:
        return health;
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-border bg-surface p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-md font-medium">Tâches actives</h3>
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Filtres</span>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium mb-1 text-muted-foreground">
              Statut opérationnel
            </label>
            <Select
              value={filters.status_ops || ""}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  status_ops: e.target.value ? (e.target.value as TaskOpsStatus) : undefined,
                })
              }
              options={[
                { value: "", label: "Tous" },
                { value: "not_started", label: "Non démarré" },
                { value: "in_progress", label: "En cours" },
                { value: "blocked", label: "Bloqué" },
                { value: "completed", label: "Terminé" },
              ]}
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1 text-muted-foreground">
              Statut de cycle de vie
            </label>
            <Select
              value={filters.status_lifecycle || ""}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  status_lifecycle: e.target.value ? (e.target.value as TaskLifecycleStatus) : undefined,
                })
              }
              options={[
                { value: "", label: "Tous" },
                { value: "active", label: "Actif" },
                { value: "paused", label: "En pause" },
                { value: "archived", label: "Archivé" },
                { value: "cancelled", label: "Annulé" },
              ]}
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1 text-muted-foreground">
              État de santé
            </label>
            <Select
              value={filters.health_state || ""}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  health_state: e.target.value ? (e.target.value as TaskHealthState) : undefined,
                })
              }
              options={[
                { value: "", label: "Tous" },
                { value: "good", label: "Bon" },
                { value: "attention", label: "Attention" },
                { value: "critical", label: "Critique" },
              ]}
            />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="p-8 text-center text-muted-foreground">Chargement...</div>
        ) : tasks.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            Aucune tâche trouvée pour ce processus
          </div>
        ) : (
          <div className="p-4 space-y-2">
            {tasks.map((task) => (
              <div
                key={task.id}
                className="p-4 border border-border rounded-lg bg-surface hover:bg-surface-subtle transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium">Tâche #{task.id.slice(0, 8)}</span>
                      <span
                        className={cn(
                          "text-xs px-2 py-0.5 rounded",
                          getStatusTone(task.status_ops) === "success" && "bg-success/10 text-success",
                          getStatusTone(task.status_ops) === "info" && "bg-info/10 text-info",
                          getStatusTone(task.status_ops) === "warning" && "bg-warning/10 text-warning",
                          getStatusTone(task.status_ops) === "neutral" && "bg-muted text-muted-foreground"
                        )}
                      >
                        {getStatusLabel(task.status_ops)}
                      </span>
                      <span className="text-xs px-2 py-0.5 bg-muted text-muted-foreground rounded">
                        {getLifecycleLabel(task.status_lifecycle)}
                      </span>
                      <div className="flex items-center gap-1">
                        {getHealthIcon(task.health_state)}
                        <span className="text-xs text-muted-foreground">
                          {getHealthLabel(task.health_state)}
                        </span>
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground space-y-1">
                      <div>Famille: {task.ops_family}</div>
                      <div>
                        Début: {new Date(task.start_date).toLocaleDateString("fr-CA")}
                        {task.target_end_date && (
                          <span className="ml-2">
                            • Fin prévue: {new Date(task.target_end_date).toLocaleDateString("fr-CA")}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {total > 50 && (
        <div className="border-t border-border p-4 flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Affichage de {tasks.length} sur {total} tâches
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Précédent
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={tasks.length < 50}
            >
              Suivant
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
